import { createConsumer } from '@rails/actioncable'
import {
  initial,
  phase,
  transition,
  validSettings,
  formatTime,
  scheduleText,
  total,
  elapsed,
} from './clock.mjs'
import { FocusAudio } from './audio'
import { createTracker } from './analytics'

const root = document.getElementById('pomodoro')
if (root) {
  const $ = (id) => document.getElementById(id)
  const read = (key, fallback) => {
    try {
      return JSON.parse(localStorage.getItem(`pomodoro:${key}`)) ?? fallback
    } catch {
      return fallback
    }
  }
  const save = (key, value) => {
    try {
      localStorage.setItem(`pomodoro:${key}`, JSON.stringify(value))
    } catch {
      /* Timer still works without storage. */
    }
  }
  const audio = new FocusAudio()
  const roomId = root.dataset.roomId
  const base = '/tools/pomodoro-timer'
  const track = createTracker(root, base)
  let room = null,
    identity = null,
    joined = false,
    consumer = null,
    cableConnected = false
  let busy = false,
    heartbeatBusy = false,
    offset = 0,
    lastPhase = null,
    fatal = false,
    teamTracked = false
  const preferences = {
    music: true,
    chime: true,
    volume: 35,
    still: matchMedia('(prefers-reduced-motion: reduce)').matches,
    ...read('preferences', {}),
  }
  preferences.volume = Number.isFinite(preferences.volume)
    ? Math.max(0, Math.min(100, preferences.volume))
    : 35
  let saved = read('solo', null)
  let solo =
    saved &&
    validSettings(saved.settings) &&
    ['ready', 'running', 'paused', 'finished'].includes(saved.status) &&
    Number.isFinite(saved.elapsed) &&
    saved.elapsed >= 0 &&
    (saved.status !== 'running' || Number.isFinite(saved.resumed_at))
      ? saved
      : initial()
  let focusViewOverride = null,
    helloUntil = 0,
    readinessBusy = false
  let musicEnabled = false // Sound requires an explicit gesture on each visit.
  let myName = read('name', '')
  const now = () => Date.now() / 1000 + (roomId ? offset : 0)
  const state = () => (roomId ? room || initial() : solo)
  const isHost = () => !roomId || (room && room.host_id === identity)
  const message = (text) => {
    $('status').textContent = text
  }
  const announce = (text) => {
    $('announcement').textContent = text
  }
  const settings = () => ({
    focus: Number($('focus').value),
    rest: Number($('rest').value),
    rounds: Number($('rounds').value),
  })
  const reflectSettings = () => {
    Object.entries(state().settings).forEach(([key, value]) => {
      $(key).value = value
    })
    $('schedule').textContent = scheduleText(state().settings)
  }

  async function request(path, body) {
    const started = Date.now()
    const response = await fetch(`${base}${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
          .content,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: AbortSignal.timeout(12000),
    })
    if (response.status === 204) return null
    let data
    try {
      data = await response.json()
    } catch {
      throw new Error(
        'Could not connect. Please refresh the page and try again.',
      )
    }
    if (!response.ok) {
      const error = new Error(
        data.error || 'Something went wrong. Please try again.',
      )
      error.status = response.status
      throw error
    }
    if (data.room)
      data.offset =
        data.room.server_now + (Date.now() - started) / 2000 - Date.now() / 1000
    return data
  }

  function accept(data, fromCable = false) {
    const incoming = fromCable ? data : data.room
    if (!incoming || (room && incoming.sequence < room.sequence)) return
    const revisionChanged = !room || incoming.revision !== room.revision
    room = incoming
    if (!teamTracked && room.members.length >= 2) {
      teamTracked = true
      track('team_active', { participants: room.members.length })
    }
    if (!fromCable) {
      identity = data.identity
      offset = data.offset
    }
    fatal = false
    if (revisionChanged) reflectSettings()
    renderPeople()
    render()
  }

  function renderPeople() {
    if (!room) return
    $('room-name').textContent = room.name
    $('people-count').textContent =
      `${room.members.length} ${room.members.length === 1 ? 'person' : 'people'} here`
    const list = $('members')
    list.replaceChildren()
    const selected = $('new-host').value
    $('new-host').replaceChildren(new Option('Choose a teammate', ''))
    room.members.forEach((member) => {
      const li = document.createElement('li'),
        avatar = document.createElement('span'),
        name = document.createElement('span'),
        role = document.createElement('span')
      avatar.className = 'avatar'
      avatar.textContent = Array.from(member.name)[0]?.toUpperCase() || '?'
      name.className = 'member-name'
      name.textContent = `${member.name}${member.id === identity ? ' (you)' : ''}`
      role.className = 'member-role'
      role.textContent =
        now() - member.last_seen > 75
          ? 'Reconnecting'
          : member.id === room.host_id
            ? member.ready && room.status === 'ready'
              ? 'Host · Ready'
              : 'Host'
            : member.ready && room.status === 'ready'
              ? 'Ready ✓'
              : 'Here'
      li.append(avatar, name, role)
      list.append(li)
      if (member.id !== identity)
        $('new-host').add(new Option(member.name, member.id))
    })
    $('new-host').value = selected
    $('host-tools').hidden = !joined || !isHost()
    $('leave').hidden = !joined
  }

  const breakCopy = [
    [
      'Time for a little movement.',
      'Try 10 jumping jacks or a gentle seated stretch. Move in a way that feels good.',
    ],
    [
      'A sip and a reset.',
      'Refill your water, take a few sips, and relax your shoulders.',
    ],
    [
      'A little fresh air?',
      'Step outside for a few breaths, or look away from your screen toward something far away.',
    ],
  ]
  function render() {
    const current = state(),
      p = phase(current, now()),
      finished = p.kind === 'finished'
    const paused = current.status === 'paused',
      ready = current.status === 'ready'
    const canControl = isHost() && (!roomId || joined) && !fatal
    const starting = p.kind === 'countdown'
    const running = !ready && !paused && !finished && !starting
    const musicSelected = preferences.music && preferences.volume > 0
    const audible = musicEnabled && musicSelected
    const musicIndicated =
      ready || finished || starting ? musicSelected : audible
    const guitar = audible && running && p.kind === 'focus'
    const rest = p.kind === 'rest'
    $('countdown').textContent = starting
      ? String(p.remaining)
      : formatTime(p.remaining)
    $('countdown').setAttribute(
      'aria-label',
      starting
        ? `Starting in ${p.remaining}`
        : `${Math.floor(p.remaining / 60)} minutes ${p.remaining % 60} seconds remaining`,
    )
    document.title = `${starting ? `Starting in ${p.remaining}` : formatTime(p.remaining)} · ${finished ? 'Nice work!' : rest ? 'Take a breather' : 'Focus'} | Team Pomodoro`
    $('round-label').textContent =
      `Round ${p.round} of ${current.settings.rounds}`
    $('mode-badge').textContent =
      `●  ${starting ? 'Starting together' : finished ? 'Session complete' : paused ? 'Taking a moment' : ready ? 'Ready to focus' : rest ? 'A well-earned break' : 'Focus time'}`
    $('character-copy').textContent =
      helloUntil > Date.now()
        ? 'Hi, focus friend!'
        : starting
          ? 'Settle in. Here we go…'
          : finished
            ? 'Nice work. You made time to focus.'
            : paused
              ? 'Take your time.'
              : ready
                ? 'Ready when you are.'
                : rest
                  ? breakCopy[p.breakType][0]
                  : 'One thing at a time.'
    $('phase-copy').textContent = finished
      ? 'Small steps add up. Take a moment to enjoy it.'
      : paused
        ? 'We’ll pick up right where you left off.'
        : rest
          ? breakCopy[p.breakType][1]
          : 'A little less rush. A little more room to think.'
    const tomato = $('tomato')
    tomato.className = `tomato ${helloUntil > Date.now() ? 'waving' : ''} ${rest && !paused ? '' : guitar ? '' : 'stopwatch'} ${running && (rest || guitar) ? 'animated' : ''}`
    tomato.dataset.row = rest ? p.breakType + 1 : 0
    tomato.setAttribute(
      'title',
      paused || finished || (!rest && !guitar)
        ? 'A friendly tomato holding a stopwatch'
        : rest
          ? [
              'A tomato doing jumping jacks',
              'A tomato drinking water',
              'A tomato taking a breath beside a plant',
            ][p.breakType]
          : 'A tomato strumming an acoustic guitar',
    )
    document.querySelector('.scene').dataset.scene = finished
      ? 'finished'
      : guitar
        ? 'music'
        : rest
          ? 'rest'
          : 'ready'
    const dots = $('round-dots')
    if (dots.childElementCount !== current.settings.rounds)
      dots.replaceChildren(
        ...Array.from({ length: current.settings.rounds }, () =>
          document.createElement('span'),
        ),
      )
    Array.from(dots.children).forEach((dot, i) => {
      dot.className =
        finished || i < p.round - 1 || (rest && i === p.round - 1)
          ? 'done'
          : i === p.round - 1
            ? 'current'
            : ''
    })
    $('primary').textContent = starting
      ? `Starting in ${p.remaining}…`
      : finished
        ? '↻  Run another session'
        : paused
          ? '▶  Resume focusing'
          : ready
            ? roomId
              ? '▶  Start together'
              : '▶  Start focusing'
            : 'Ⅱ  Pause session'
    $('primary').disabled = !canControl || busy || starting
    $('reset').disabled = !canControl || ready || busy
    $('skip').disabled = !canControl || ready || finished || busy || starting
    $('settings-fields').disabled = !canControl || busy
    const dirty =
      JSON.stringify(settings()) !== JSON.stringify(current.settings)
    $('apply-settings').hidden = !canControl || (!dirty && (ready || finished))
    $('apply-settings').disabled = busy
    $('apply-settings').textContent =
      ready || finished ? 'Apply settings' : 'Apply & restart session'
    $('settings-note').textContent = !canControl
      ? 'The host chooses the shared schedule.'
      : !ready && !finished
        ? 'Edit your next schedule below. Apply & restart resets the current session.'
        : 'Choose a preset or type your own times.'
    document.querySelectorAll('.presets button').forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        String(
          Number(button.dataset.focus) === settings().focus &&
            Number(button.dataset.rest) === settings().rest,
        ),
      )
    })
    const nextBreak = ['movement', 'water', 'fresh-air'][p.breakType]
    $('up-next').textContent = starting
      ? `${current.settings.focus} minutes of focus ahead`
      : finished
        ? ''
        : rest
          ? `${paused ? 'Next focus round in' : 'Next focus starts in'} ${formatTime(p.remaining)}${paused ? ' · paused' : ''}`
          : p.round === current.settings.rounds
            ? 'Last focus round — then you’re done.'
            : `Up next: ${current.settings.rest}-minute ${nextBreak} break`
    const remaining = total(current.settings) - elapsed(current, now())
    const finishAt = new Date(
      (now() + remaining + (starting ? current.resumed_at - now() : 0)) * 1000,
    )
    $('finish-time').textContent = finished
      ? ''
      : paused
        ? `${Math.ceil(remaining / 60)} min left · finish time updates when you resume`
        : `${ready ? 'Start now · finish' : 'Finished by'} ${finishAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    const focused =
      focusViewOverride ?? (!ready && !finished && (!roomId || joined))
    root.classList.toggle('focus-view', focused)
    $('focus-view').setAttribute('aria-pressed', String(focused))
    $('focus-view').textContent = focused ? 'Show full page' : 'Focus view'
    $('compact-team').hidden = !roomId || !joined
    $('compact-team').textContent = room
      ? `${room.members.length} ${room.members.length === 1 ? 'person' : 'people'} focusing together`
      : ''
    $('compact-team').title =
      room?.members.map((member) => member.name).join(', ') || ''
    $('lobby').hidden = !roomId || !joined || !ready
    if (room) {
      const me = room.members.find((member) => member.id === identity)
      $('ready-check').setAttribute('aria-pressed', String(!!me?.ready))
      $('ready-check').textContent = me?.ready ? 'Ready ✓ · undo' : 'I’m ready'
      $('ready-check').disabled = readinessBusy
      $('ready-count').textContent =
        `${room.members.filter((member) => member.ready).length} of ${room.members.length} ready`
    }
    $('host-note').hidden = !roomId || isHost() || !joined
    $('finished-actions').hidden = !finished
    $('music').setAttribute('aria-pressed', String(musicIndicated))
    const musicAction = musicIndicated
      ? 'Turn focus music off'
      : 'Turn focus music on'
    $('music').setAttribute('aria-label', musicAction)
    $('music').title = musicAction
    $('bell').setAttribute('aria-pressed', String(preferences.chime))
    $('bell').querySelector('span').textContent = preferences.chime
      ? 'Chime on'
      : 'Chime off'
    root.classList.toggle('still', preferences.still)
    audio.setPlaying(audible && running && !rest, preferences.volume / 100)
    if (starting && lastPhase?.remaining !== p.remaining)
      announce(`Starting in ${p.remaining}`)
    if (
      lastPhase &&
      lastPhase.index !== p.index &&
      runningOrFinished(lastPhase.status) &&
      !ready
    ) {
      if (preferences.chime) audio.chime()
      announce(
        finished
          ? 'Session complete. Nice work!'
          : rest
            ? `Break time. ${breakCopy[p.breakType][0]}`
            : `Focus round ${p.round} is starting.`,
      )
      // Host skip/end controls should not be recorded as completed focus time.
      if (lastPhase.revision === current.revision) {
        if (lastPhase.kind === 'focus')
          track('focus_completed', {
            mode: roomId ? 'team' : 'solo',
            round: lastPhase.round,
          })
        if (finished)
          track('session_completed', {
            mode: roomId ? 'team' : 'solo',
            rounds: current.settings.rounds,
          })
      }
    }
    lastPhase = {
      index: p.index,
      remaining: p.remaining,
      kind: p.kind,
      round: p.round,
      status: current.status,
      revision: current.revision,
    }
  }
  function runningOrFinished(status) {
    return ['running', 'paused', 'finished'].includes(status)
  }

  async function command(action, extra = {}) {
    if (busy || !isHost()) return false
    busy = true
    render()
    message('')
    try {
      if (roomId)
        accept(
          await request(`/rooms/${roomId}/control`, {
            command: action,
            revision: room.revision,
            ...extra,
          }),
        )
      else {
        solo =
          action === 'reset' && extra.settings
            ? initial(extra.settings)
            : transition(solo, action, now())
        save('solo', solo)
      }
      if (action === 'reset') {
        reflectSettings()
        focusViewOverride = null
      }
      if (action === 'start')
        track('session_started', {
          mode: roomId ? 'team' : 'solo',
          rounds: state().settings.rounds,
        })
      return true
    } catch (error) {
      message(error.message)
      if (roomId && error.status === 409) {
        try {
          accept(await request(`/rooms/${roomId}/state`))
        } catch {
          /* Existing error remains visible. */
        }
      }
      return false
    } finally {
      busy = false
      render()
    }
  }
  async function unlockAudio() {
    try {
      const ready = await Promise.race([
        audio.unlock().then(() => audio.context.state === 'running'),
        new Promise((resolve) => setTimeout(() => resolve(false), 1500)),
      ])
      if (!ready) throw new Error('Audio did not start')
      return true
    } catch {
      message(
        'Sound is unavailable in this browser. The timer will keep running quietly.',
      )
      return false
    }
  }

  $('primary').addEventListener('click', async () => {
    if (
      (state().status === 'ready' ||
        phase(state(), now()).kind === 'finished') &&
      !$('settings-form').reportValidity()
    )
      return
    const soundReady = await unlockAudio()
    if (preferences.music) musicEnabled = soundReady
    const current = state(),
      p = phase(current, now())
    if (p.kind === 'finished') {
      if (await command('reset', { settings: settings() }))
        await command('start')
    } else {
      if (
        roomId &&
        current.status === 'ready' &&
        JSON.stringify(settings()) !== JSON.stringify(current.settings)
      ) {
        if (!(await command('reset', { settings: settings() }))) return
      }
      await command(
        current.status === 'ready'
          ? 'start'
          : current.status === 'paused'
            ? 'resume'
            : 'pause',
      )
    }
  })
  $('reset').addEventListener('click', () => {
    if (
      confirm(
        roomId
          ? 'Reset the timer for everyone in this room?'
          : 'Reset this session?',
      )
    )
      command('reset')
  })
  $('skip').addEventListener('click', () => {
    if (
      confirm(
        roomId
          ? 'Skip this session for everyone?'
          : 'Skip to the next session?',
      )
    )
      command('skip')
  })
  $('end').addEventListener('click', () => {
    if (confirm('End this session for everyone?')) command('end')
  })
  $('transfer').addEventListener('click', () => {
    if (
      $('new-host').value &&
      confirm('Give this teammate control of the timer?')
    )
      command('transfer', { member_id: $('new-host').value })
  })
  $('music').addEventListener('click', async () => {
    const awaitingGesture =
      state().status !== 'ready' &&
      phase(state(), now()).kind !== 'finished' &&
      !musicEnabled
    const enable =
      !preferences.music || preferences.volume === 0 || awaitingGesture
    if (enable && !(await unlockAudio())) return
    musicEnabled = enable
    if (enable && preferences.volume === 0) {
      preferences.volume = 35
      $('volume').value = preferences.volume
    }
    preferences.music = musicEnabled
    save('preferences', preferences)
    render()
  })
  $('bell').addEventListener('click', async () => {
    await unlockAudio()
    preferences.chime = !preferences.chime
    save('preferences', preferences)
    render()
  })
  $('volume').value = preferences.volume
  $('volume').addEventListener('input', (event) => {
    preferences.volume = Number(event.target.value)
    save('preferences', preferences)
    render()
  })
  $('reduced-motion').checked = preferences.still
  $('reduced-motion').addEventListener('change', (event) => {
    preferences.still = event.target.checked
    save('preferences', preferences)
    render()
  })

  function changeSettings() {
    const next = settings()
    if (!validSettings(next)) return
    $('schedule').textContent = scheduleText(next)
    if (
      !roomId &&
      (solo.status === 'ready' || phase(solo, now()).kind === 'finished')
    ) {
      solo = initial(next)
      save('solo', solo)
      render()
    }
  }
  $('settings-form').addEventListener('input', changeSettings)
  $('settings-form').addEventListener('submit', (event) => {
    event.preventDefault()
    if (!validSettings(settings())) return
    const active =
      state().status !== 'ready' && phase(state(), now()).kind !== 'finished'
    if (
      active &&
      !confirm(
        roomId
          ? 'Restart the session for everyone with these settings?'
          : 'Restart this session with these settings?',
      )
    )
      return
    command('reset', { settings: settings() })
  })
  document.querySelectorAll('.presets button').forEach((button) => {
    button.addEventListener('click', () => {
      $('focus').value = button.dataset.focus
      $('rest').value = button.dataset.rest
      changeSettings()
      render()
    })
  })
  $('focus-view').addEventListener('click', () => {
    focusViewOverride = !root.classList.contains('focus-view')
    render()
  })
  $('fullscreen').hidden = !document.fullscreenEnabled
  $('fullscreen').addEventListener('click', async () => {
    let timeout
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else {
        focusViewOverride = true
        render()
        await Promise.race([
          root.requestFullscreen(),
          new Promise((_, reject) => {
            timeout = setTimeout(
              () => reject(new Error('Fullscreen unavailable')),
              3000,
            )
          }),
        ])
      }
    } catch {
      message('Full screen is unavailable. You can still use Focus view.')
    } finally {
      clearTimeout(timeout)
    }
  })
  document.addEventListener('fullscreenchange', () => {
    $('fullscreen').textContent = document.fullscreenElement
      ? 'Exit full screen'
      : 'Full screen'
  })
  $('tomato').addEventListener('click', () => {
    helloUntil = Date.now() + 1800
    announce('Hi, focus friend!')
    render()
  })
  $('ready-check').addEventListener('click', async () => {
    if (readinessBusy || !joined) return
    readinessBusy = true
    render()
    try {
      const me = room.members.find((member) => member.id === identity)
      accept(await request(`/rooms/${roomId}/ready`, { ready: !me?.ready }))
    } catch (error) {
      message(error.message)
    } finally {
      readinessBusy = false
      render()
    }
  })

  $('create-team').addEventListener('click', () => {
    if (!$('settings-form').reportValidity()) return
    $('display-name').value = myName
    $('create-schedule').textContent = scheduleText(settings())
    $('create-error').textContent = ''
    $('create-dialog').showModal()
  })
  $('close-dialog').addEventListener('click', () => $('create-dialog').close())
  $('create-form').addEventListener('submit', async (event) => {
    event.preventDefault()
    $('create-submit').disabled = true
    try {
      myName =
        $('display-name').value.trim() ||
        `Guest ${Math.floor(Math.random() * 900) + 100}`
      save('name', myName)
      const data = await request('/rooms', {
        ...settings(),
        display_name: myName,
        name: $('team-name').value,
      })
      save(`joined:${data.room.id}`, true)
      track('room_created')
      location.assign(`${base}/rooms/${data.room.id}`)
    } catch (error) {
      $('create-error').textContent = error.message
    } finally {
      $('create-submit').disabled = false
    }
  })
  $('copy-link').addEventListener('click', async () => {
    const link = `${location.origin}${base}/rooms/${roomId}`
    try {
      await navigator.clipboard.writeText(link)
      $('copy-link').textContent = 'Link copied ✓'
      track('invite_copied')
      setTimeout(() => {
        $('copy-link').textContent = 'Copy invite link ↗'
      }, 2500)
    } catch {
      prompt('Copy this invitation link:', link)
    }
  })

  function connect() {
    if (consumer) return
    consumer = createConsumer('/cable')
    consumer.subscriptions.create(
      { channel: 'PomodoroChannel', room_id: roomId },
      {
        connected() {
          cableConnected = true
          $('connection').textContent = '● Connected · shared timer'
          heartbeat()
        },
        disconnected() {
          cableConnected = false
          $('connection').textContent = 'Reconnecting · keeping your place'
          track('connection_interrupted')
        },
        rejected() {
          cableConnected = false
          $('connection').textContent = 'Connected · periodically syncing'
        },
        received(data) {
          accept(data, true)
        },
      },
    )
  }
  async function join() {
    if (busy || fatal) return
    busy = true
    $('join').disabled = true
    try {
      const soundReady = await unlockAudio()
      myName =
        $('join-name').value.trim() ||
        myName ||
        `Guest ${Math.floor(Math.random() * 900) + 100}`
      save('name', myName)
      accept(await request(`/rooms/${roomId}/join`, { display_name: myName }))
      joined = true
      save(`joined:${roomId}`, true)
      $('connection').textContent = 'Connecting to your team…'
      $('join-panel').hidden = true
      if (preferences.music) musicEnabled = soundReady
      connect()
      renderPeople()
      message('')
      track('room_joined', { participants: room.members.length })
    } catch (error) {
      message(error.message)
    } finally {
      busy = false
      $('join').disabled = false
      render()
    }
  }
  $('join').addEventListener('click', join)
  $('join-name').addEventListener('keydown', (event) => {
    if (event.key === 'Enter') join()
  })
  $('leave').addEventListener('click', async () => {
    try {
      await request(`/rooms/${roomId}/leave`, {})
      save(`joined:${roomId}`, false)
      consumer?.disconnect()
      location.assign(base)
    } catch (error) {
      message(error.message)
    }
  })
  async function heartbeat() {
    if (heartbeatBusy || fatal || !joined) return
    heartbeatBusy = true
    try {
      accept(
        await request(`/rooms/${roomId}/heartbeat`, { display_name: myName }),
      )
      $('connection').textContent = cableConnected
        ? '● Connected · shared timer'
        : '● Connected · periodically syncing'
    } catch (error) {
      $('connection').textContent = 'Reconnecting · keeping your place'
      if (error.status === 404) expired(error.message)
    } finally {
      heartbeatBusy = false
    }
  }
  function expired(text) {
    fatal = true
    joined = false
    consumer?.disconnect()
    $('join-panel').hidden = true
    $('connection').textContent = 'Room unavailable'
    message(text)
    const link = document.createElement('a')
    link.href = base
    link.textContent = ' Start a new session.'
    $('status').append(link)
    render()
  }
  if (roomId) {
    $('team-intro').hidden = true
    $('room-panel').hidden = false
    $('team-title').textContent = 'Your focus room'
    $('join-name').value = myName
    $('join-panel').hidden = false
    request(`/rooms/${roomId}/state`)
      .then(async (data) => {
        accept(data)
        if (read(`joined:${roomId}`, false)) {
          joined = true
          $('join-panel').hidden = true
          await heartbeat()
          if (!fatal) {
            connect()
            renderPeople()
            render()
          }
        } else $('connection').textContent = 'Join to focus with the team'
      })
      .catch((error) => expired(error.message))
    setInterval(heartbeat, 30000)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) heartbeat()
    })
    window.addEventListener('online', heartbeat)
  }
  // Presence expires server-side. Never remove it on tab blur or unload:
  // another tab may still be connected, and unload is unreliable on mobile.
  window.addEventListener('pagehide', () => {
    consumer?.disconnect()
    audio.setPlaying(false)
  })
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && roomId) {
      consumer?.connect()
      heartbeat()
    }
  })
  reflectSettings()
  render()
  setInterval(render, 250)
}
